import supabase from '../lib/supabase';

// Table name with random suffix to avoid conflicts
const TABLE_NAME = 'bookmarks_ai6hd7f9p';

class SupabaseService {
  constructor() {
    this.isOnline = false;
    this.checkConnection();
  }

  /**
   * Check if Supabase is accessible
   */
  async checkConnection() {
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select('count(*)')
        .limit(1)
        .single();

      this.isOnline = !error;
      if (this.isOnline) {
        console.log('✅ Supabase connection established');
      } else {
        console.warn('⚠️ Supabase connection failed:', error.message);
      }
      return this.isOnline;
    } catch (error) {
      this.isOnline = false;
      console.warn('⚠️ Supabase connection failed:', error.message);
      return false;
    }
  }

  /**
   * Get all bookmarks from Supabase
   * @returns {Promise<Object>} - Result with bookmarks array
   */
  async getAllBookmarks() {
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(error.message);
      }

      // Transform data to match the expected format
      const bookmarks = data.map(bookmark => ({
        id: bookmark.id,
        name: bookmark.name,
        url: bookmark.url,
        description: bookmark.description || '',
        commentary: bookmark.commentary || '',
        tags: bookmark.tags || [],
        favorite: bookmark.favorite || false,
        favicon: bookmark.favicon || `https://www.google.com/s2/favicons?domain=${new URL(bookmark.url).hostname}&sz=32`,
        createdAt: bookmark.created_at,
        updatedAt: bookmark.updated_at
      }));

      return {
        success: true,
        bookmarks,
        lastModified: new Date().toISOString(),
        count: bookmarks.length
      };
    } catch (error) {
      console.error('Error getting bookmarks from Supabase:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Save all bookmarks to Supabase
   * @param {Array} bookmarks - Array of bookmark objects
   * @returns {Promise<Object>} - Result with success status
   */
  async saveBookmarks(bookmarks) {
    try {
      // First delete all existing bookmarks
      const { error: deleteError } = await supabase
        .from(TABLE_NAME)
        .delete()
        .neq('id', 'placeholder'); // Delete all rows
      
      if (deleteError) {
        throw new Error(`Error deleting existing bookmarks: ${deleteError.message}`);
      }

      // Then insert all bookmarks
      const { data, error: insertError } = await supabase
        .from(TABLE_NAME)
        .insert(
          bookmarks.map(bookmark => ({
            id: bookmark.id,
            name: bookmark.name,
            url: bookmark.url,
            description: bookmark.description || '',
            commentary: bookmark.commentary || '',
            tags: bookmark.tags || [],
            favorite: bookmark.favorite || false,
            favicon: bookmark.favicon || `https://www.google.com/s2/favicons?domain=${new URL(bookmark.url).hostname}&sz=32`
          }))
        )
        .select();

      if (insertError) {
        throw new Error(`Error inserting bookmarks: ${insertError.message}`);
      }

      return {
        success: true,
        message: `Saved ${bookmarks.length} bookmarks to Supabase`,
        count: bookmarks.length,
        lastModified: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error saving bookmarks to Supabase:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Add a single bookmark to Supabase
   * @param {Object} bookmark - Bookmark object
   * @returns {Promise<Object>} - Result with success status
   */
  async addBookmark(bookmark) {
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .insert({
          id: bookmark.id,
          name: bookmark.name,
          url: bookmark.url,
          description: bookmark.description || '',
          commentary: bookmark.commentary || '',
          tags: bookmark.tags || [],
          favorite: bookmark.favorite || false,
          favicon: bookmark.favicon || `https://www.google.com/s2/favicons?domain=${new URL(bookmark.url).hostname}&sz=32`
        })
        .select()
        .single();

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        bookmark: {
          id: data.id,
          name: data.name,
          url: data.url,
          description: data.description || '',
          commentary: data.commentary || '',
          tags: data.tags || [],
          favorite: data.favorite || false,
          favicon: data.favicon || `https://www.google.com/s2/favicons?domain=${new URL(data.url).hostname}&sz=32`,
          createdAt: data.created_at,
          updatedAt: data.updated_at
        },
        message: 'Bookmark added successfully'
      };
    } catch (error) {
      console.error('Error adding bookmark to Supabase:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Update a bookmark in Supabase
   * @param {string} id - Bookmark ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} - Result with success status
   */
  async updateBookmark(id, updates) {
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .update({
          name: updates.name,
          url: updates.url,
          description: updates.description || '',
          commentary: updates.commentary || '',
          tags: updates.tags || [],
          favorite: updates.favorite || false,
          favicon: updates.favicon || `https://www.google.com/s2/favicons?domain=${new URL(updates.url).hostname}&sz=32`,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        bookmark: {
          id: data.id,
          name: data.name,
          url: data.url,
          description: data.description || '',
          commentary: data.commentary || '',
          tags: data.tags || [],
          favorite: data.favorite || false,
          favicon: data.favicon || `https://www.google.com/s2/favicons?domain=${new URL(data.url).hostname}&sz=32`,
          createdAt: data.created_at,
          updatedAt: data.updated_at
        },
        message: 'Bookmark updated successfully'
      };
    } catch (error) {
      console.error('Error updating bookmark in Supabase:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Delete a bookmark from Supabase
   * @param {string} id - Bookmark ID
   * @returns {Promise<Object>} - Result with success status
   */
  async deleteBookmark(id) {
    try {
      const { error } = await supabase
        .from(TABLE_NAME)
        .delete()
        .eq('id', id);

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Bookmark deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting bookmark from Supabase:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Export bookmarks from Supabase
   * @returns {Promise<Object>} - Result with success status
   */
  async exportBookmarks() {
    try {
      const result = await this.getAllBookmarks();
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      const exportData = {
        version: '1.0',
        exportDate: new Date().toISOString(),
        bookmarksCount: result.bookmarks.length,
        bookmarks: result.bookmarks,
        source: 'supabase'
      };
      
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ai-apps-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      return {
        success: true,
        message: 'Export downloaded successfully'
      };
    } catch (error) {
      console.error('Error exporting from Supabase:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get statistics about bookmarks in Supabase
   * @returns {Promise<Object>} - Result with statistics
   */
  async getStatistics() {
    try {
      const result = await this.getAllBookmarks();
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      const bookmarks = result.bookmarks;
      const totalBookmarks = bookmarks.length;
      const favoriteBookmarks = bookmarks.filter(b => b.favorite).length;
      
      // Count tags
      const tagCounts = {};
      bookmarks.forEach(bookmark => {
        if (bookmark.tags && Array.isArray(bookmark.tags)) {
          bookmark.tags.forEach(tag => {
            tagCounts[tag] = (tagCounts[tag] || 0) + 1;
          });
        }
      });
      
      const mostUsedTags = Object.entries(tagCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10)
        .map(([tag, count]) => ({ tag, count }));
      
      return {
        success: true,
        stats: {
          totalBookmarks,
          favoriteBookmarks,
          totalTags: Object.keys(tagCounts).length,
          mostUsedTags,
          lastModified: result.lastModified
        }
      };
    } catch (error) {
      console.error('Error getting statistics from Supabase:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get storage info
   */
  getStorageInfo() {
    return {
      provider: 'Supabase',
      type: 'PostgreSQL Database',
      endpoint: supabase.supabaseUrl,
      tableName: TABLE_NAME,
      authenticated: true, // Using anon key
      features: [
        'Secure PostgreSQL database',
        'Realtime updates capability',
        'Cross-device synchronization',
        'Structured data storage',
        'Row-level security'
      ]
    };
  }

  /**
   * Check if Supabase is currently online
   */
  isApiOnline() {
    return this.isOnline;
  }
}

// Create singleton instance
const supabaseService = new SupabaseService();
export default supabaseService;